import React from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';

// Route Components
// Study list variants are selected by the `workList.variant` customization:
// - `'legacy'`  → LegacyWorkList (the pre-3.13 study list)
// - anything else (including `'default'`) → WorkList (ui-next study list)
import WorkList from './WorkList/WorkList';
import LegacyWorkList from './LegacyWorkList/LegacyWorkList';
import DataSourceWrapper from './DataSourceWrapper';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import Login from './Login';
import Dashboard from './Dashboard';
import AdminDashboard from './AdminDashboard';
import ProjectCreation from './ProjectCreation';
import ProjectCreationRouteCache from './ProjectCreation/ProjectCreationRouteCache';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import RoleRoute from './RoleRoute';
import type { UserRole } from '../services/authService';
import PropTypes from 'prop-types';
import { routerBasename } from '../utils/publicUrl';
import { useAppConfig } from '@state';
import { history } from '../utils/history';

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="text-foreground absolute flex h-full w-full items-center justify-center">
      <div>
        <h4>{message}</h4>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  const [appConfig] = useAppConfig();
  const { showStudyList } = appConfig;

  return (
    <div className="text-foreground absolute flex h-full w-full items-center justify-center">
      <div>
        <h4 data-cy="study-not-found-message">
          One or more of the requested studies are not available at this time.
        </h4>
        {showStudyList && (
          <p
            className="mt-2"
            data-cy="return-to-study-list-message"
          >
            Return to the{' '}
            <Link
              className="text-highlight"
              to="/"
            >
              study list
            </Link>{' '}
            to select a different study to view.
          </p>
        )}
      </div>
    </div>
  );
};

NotFoundStudy.propTypes = {
  message: PropTypes.string,
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  {
    path: `/notfoundserver`,
    children: NotFoundServer,
  },
  {
    path: `/notfoundstudy`,
    children: NotFoundStudy,
  },
  {
    path: `/debug`,
    children: Debug,
  },
  {
    path: `/local`,
    children: Local.bind(null, { modePath: '' }), // navigate to the worklist
  },
  {
    path: `/localbasic`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  },
];

// NOT FOUND (404)
const notFoundRoute = { path: '*', children: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const path =
    routerBasename.length > 1 && routerBasename.endsWith('/')
      ? routerBasename.substring(0, routerBasename.length - 1)
      : routerBasename;

  console.log('Registering worklist route', routerBasename, path);

  const workListVariant = customizationService.getCustomization('workList.variant');
  const WorkListComponent = workListVariant === 'legacy' ? LegacyWorkList : WorkList;

  const WorkListRoute = {
    path: '/studies',
    children: DataSourceWrapper,
    private: true,
    props: { children: WorkListComponent, servicesManager, extensionManager },
  };

  const LoginRoute = {
    path: '/',
    children: Login,
    private: false,
  };

  const DashboardRoute = {
    path: '/dashboard',
    children: Dashboard,
    private: true,
    roles: ['doctor'] as UserRole[],
  };

  const AdminRoute = {
    path: '/admin',
    children: AdminDashboard,
    private: true,
    roles: ['admin'] as UserRole[],
  };

  const ProjectCreationRoute = {
    path: '/project-creation',
    children: ProjectCreation,
    private: true,
    roles: ['doctor'] as UserRole[],
  };

  const ProjectCreationRoutePlaceholder = {
    ...ProjectCreationRoute,
    children: () => null,
  };

  type AppRoute = {
    path: string;
    children: React.ComponentType<any>;
    private?: boolean;
    roles?: UserRole[];
    props?: Record<string, any>;
  };

  type CustomRoutesConfig = {
    routes?: AppRoute[];
    notFoundRoute?: AppRoute;
  };

  const rawCustomRoutes = customizationService.getCustomization('routes.customRoutes');

  const customRoutes: CustomRoutesConfig =
    rawCustomRoutes && typeof rawCustomRoutes === 'object' && !Array.isArray(rawCustomRoutes)
      ? (rawCustomRoutes as CustomRoutesConfig)
      : {};

  const allRoutes = [
    ...routes,
    ...(showStudyList ? [WorkListRoute] : []),
    LoginRoute,
    AdminRoute,
    DashboardRoute,
    ProjectCreationRoutePlaceholder,
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    const [appConfig] = useAppConfig();
    const { showErrorDetails } = appConfig;

    history.navigate = useNavigate();

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <ErrorBoundary
        context={`Route ${route.path}`}
        showErrorDetails={showErrorDetails}
      >
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <>
      <Routes>
        {allRoutes.map((route, i) => {
          return route.private === true ? (
            <Route
              key={i}
              path={route.path}
              element={
                <PrivateRoute>
                  <RoleRoute allowedRoles={route.roles || ['doctor', 'admin']}>
                    <RouteWithErrorBoundary route={route} />
                  </RoleRoute>
                </PrivateRoute>
              }
            ></Route>
          ) : (
            <Route
              key={i}
              path={route.path}
              element={<RouteWithErrorBoundary route={route} />}
            />
          );
        })}
      </Routes>
      <ProjectCreationRouteCache path={ProjectCreationRoute.path}>
        {cachedLocation => (
          <RouteWithErrorBoundary
            key={(cachedLocation.state as { uploadId?: string } | null)?.uploadId}
            route={ProjectCreationRoute}
            routeLocation={cachedLocation}
          />
        )}
      </ProjectCreationRouteCache>
    </>
  );
};

export default createRoutes;
